import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ShoppingBag, Clock, CalendarCheck, UserCircle, LogOut, Sparkles, ImageOff, BookOpen, Repeat,
} from "lucide-react";
import api, { eur, getCustomerToken, clearCustomerToken } from "../api";
import { usePageTitle } from "../components/common";

const STATUS_STYLE = {
  "Paiement reçu": "badge-soft",
  "Informations à compléter": "badge-gold",
  "Informations reçues": "badge-soft",
  Réservée: "badge-soft",
  "En cours": "badge-gold",
  "Compte rendu envoyé": "badge-soft",
  Terminée: "badge-soft",
};

function Login({ onSent }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/auth/magic-link", { email });
      onSent(email, data.debug_token);
    } catch {
      toast.error("Une erreur est survenue.");
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="container-x py-16 sm:py-24 fade-up max-w-md">
      <div className="text-center mb-8">
        <Sparkles className="mx-auto text-[#C9A56A]" size={30} />
        <h1 className="mt-4 font-serif text-3xl font-medium text-[#5A3A4B]">Mon espace</h1>
        <p className="mt-2 text-sm text-[#7A5D58]">
          Reçois un lien de connexion sécurisé par e-mail. Pas de mot de passe à retenir.
        </p>
      </div>
      <form onSubmit={submit} className="card-soft p-7 space-y-4" data-testid="magiclink-form">
        <div>
          <label className="field-label">Ton email</label>
          <input required type="email" className="field-input" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="magiclink-email" />
        </div>
        <button type="submit" disabled={sending} className="btn-primary w-full" data-testid="magiclink-submit">
          {sending ? "Envoi…" : "Recevoir mon lien de connexion"}
        </button>
      </form>
    </div>
  );
}

export default function Account() {
  usePageTitle("Mon espace");
  const [authed, setAuthed] = useState(!!getCustomerToken());
  const [sent, setSent] = useState(null);
  const [tab, setTab] = useState("achats");
  const [orders, setOrders] = useState([]);
  const [info, setInfo] = useState({ first_name: "", last_name: "", phone: "", email: "" });
  const [content, setContent] = useState(null);

  const load = () => {
    api.get("/me/orders").then((r) => setOrders(r.data)).catch(() => { clearCustomerToken(); setAuthed(false); });
    api.get("/me/info").then((r) => setInfo(r.data)).catch(() => {});
    api.get("/me/content").then((r) => setContent(r.data)).catch(() => {});
  };
  useEffect(() => { if (authed) load(); }, [authed]);

  if (!authed) {
    if (sent) {
      return (
        <div className="container-x py-20 text-center fade-up max-w-md">
          <Sparkles className="mx-auto text-[#C9A56A]" size={30} />
          <h1 className="mt-4 font-serif text-2xl text-[#5A3A4B]">Vérifie ta boîte mail ✨</h1>
          <p className="mt-3 text-sm text-[#7A5D58]">
            Si un compte existe pour <strong>{sent.email}</strong>, un lien de connexion vient d'être envoyé.
          </p>
          {sent.debug && (
            <button
              onClick={() => (window.location.href = `/espace/verifier?token=${sent.debug}`)}
              className="btn-secondary mt-6"
              data-testid="magiclink-debug"
            >
              Lien de connexion (mode test)
            </button>
          )}
        </div>
      );
    }
    return <Login onSent={(email, debug) => setSent({ email, debug })} />;
  }

  const saveInfo = async () => {
    try {
      await api.put("/me/info", { first_name: info.first_name, last_name: info.last_name, phone: info.phone });
      toast.success("Informations mises à jour ✨");
    } catch {
      toast.error("Erreur lors de la sauvegarde.");
    }
  };

  const withBooking = orders.filter((o) => o.booking_required);
  const cancelSub = async () => {
    try {
      await api.post("/me/subscription/cancel");
      toast.success("Ton abonnement sera résilié à la fin de la période en cours.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "La résiliation a échoué.");
    }
  };
  const TABS = [
    { k: "achats", label: "Mes achats", icon: ShoppingBag },
    { k: "contenus", label: "Mes contenus", icon: BookOpen },
    { k: "seances", label: "Mes séances", icon: Clock },
    { k: "reservations", label: "Mes réservations", icon: CalendarCheck },
    { k: "infos", label: "Mes informations", icon: UserCircle },
  ];

  return (
    <div className="container-x py-10 sm:py-16 fade-up">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-3xl sm:text-4xl font-medium text-[#5A3A4B]">Mon espace</h1>
        <button
          onClick={() => { clearCustomerToken(); setAuthed(false); setSent(null); }}
          className="inline-flex items-center gap-2 text-sm text-[#7A5D58] hover:text-[#5A3A4B]"
          data-testid="account-logout"
        >
          <LogOut size={16} /> Déconnexion
        </button>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2 mb-8">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`account-tab-${t.k}`}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium whitespace-nowrap transition ${
                tab === t.k ? "bg-[#5A3A4B] text-[#FCFAF7]" : "bg-white text-[#5A3A4B] border border-[#7A5D58]/15"
              }`}
            >
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>

      {(tab === "achats" || tab === "seances") && (
        <div className="space-y-4" data-testid="account-orders">
          {orders.length === 0 && <p className="text-[#7A5D58]">Aucune commande pour le moment.</p>}
          {orders.map((o) => (
            <div key={o.order_id} className="card-soft p-5 sm:p-6" data-testid={`order-${o.order_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="font-serif text-xl font-semibold text-[#5A3A4B]">{o.service_name}</h3>
                  <p className="text-sm text-[#7A5D58] mt-0.5">
                    {eur(o.amount_cents)} · {o.paid_at ? new Date(o.paid_at).toLocaleDateString("fr-FR") : "—"}
                    {o.delivery_method ? ` · ${o.delivery_method === "whatsapp" ? "WhatsApp" : "Email"}` : ""}
                  </p>
                </div>
                <span className={STATUS_STYLE[o.session_status_label] || "badge-soft"}>{o.session_status_label}</span>
              </div>
              {tab === "seances" && o.question_or_intention && (
                <p className="mt-3 text-sm text-[#7A5D58] bg-[#FCFAF7] rounded-xl p-3 italic">« {o.question_or_intention} »</p>
              )}
              {o.booking_start && (
                <p className="mt-3 text-sm text-[#5A3A4B]">Créneau : {new Date(o.booking_start).toLocaleString("fr-FR")}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "contenus" && (
        <div className="space-y-4" data-testid="account-content">
          {/* Guide Tarot */}
          <div className="card-soft p-5 sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-[#C9A56A]/15 flex items-center justify-center shrink-0">
                  <BookOpen size={18} className="text-[#8a6a2f]" />
                </span>
                <h3 className="font-serif text-xl font-semibold text-[#5A3A4B]">Le Grimoire Visuel du Tarot</h3>
              </div>
              {content?.guide?.owned ? (
                content.guide.access_link ? (
                  <a href={content.guide.access_link} target="_blank" rel="noreferrer" className="btn-primary" data-testid="guide-access-btn">
                    Accéder à mon guide
                  </a>
                ) : (
                  <span className="text-sm text-[#7A5D58]" data-testid="guide-pending">Ton guide sera disponible ici très bientôt.</span>
                )
              ) : (
                <a href="/guide-du-tarot" className="btn-secondary" data-testid="guide-discover-btn">Découvrir le guide</a>
              )}
            </div>
          </div>

          {/* Subscription Entre Lui et Moi */}
          {content?.subscription && (
            <div className="card-soft p-5 sm:p-6" data-testid="account-subscription">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-[#C9A56A]/15 flex items-center justify-center shrink-0">
                    <Repeat size={18} className="text-[#8a6a2f]" />
                  </span>
                  <div>
                    <h3 className="font-serif text-xl font-semibold text-[#5A3A4B]">Entre Lui et Moi</h3>
                    <p className="text-sm text-[#7A5D58]">
                      {content.subscription.cancel_at_period_end
                        ? "Résiliation programmée — actif jusqu'à la fin de la période"
                        : content.subscription.status === "active" ? "Abonnement actif" : `Statut : ${content.subscription.status}`}
                      {content.subscription.current_period_end
                        ? ` · prochaine échéance le ${new Date(content.subscription.current_period_end).toLocaleDateString("fr-FR")}`
                        : ""}
                    </p>
                  </div>
                </div>
                {content.subscription.status === "active" && !content.subscription.cancel_at_period_end && (
                  <button onClick={cancelSub} className="btn-secondary" data-testid="sub-cancel-btn">Résilier mon abonnement</button>
                )}
              </div>
            </div>
          )}
          {!content?.guide?.owned && !content?.subscription && (
            <p className="text-[#7A5D58]">Aucun contenu numérique pour le moment.</p>
          )}
        </div>
      )}

      {tab === "reservations" && (
        <div className="space-y-4" data-testid="account-reservations">
          {withBooking.length === 0 && <p className="text-[#7A5D58]">Aucune réservation nécessitant un créneau.</p>}
          {withBooking.map((o) => (
            <div key={o.order_id} className="card-soft p-5 sm:p-6 flex items-center justify-between">
              <div>
                <h3 className="font-serif text-lg font-semibold text-[#5A3A4B]">{o.service_name}</h3>
                <p className="text-sm text-[#7A5D58]">
                  {o.booking_done ? "Créneau réservé" : "Créneau à réserver"}
                </p>
              </div>
              <span className={STATUS_STYLE[o.session_status_label] || "badge-soft"}>{o.session_status_label}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "infos" && (
        <div className="card-soft p-6 max-w-lg space-y-4" data-testid="account-info">
          <div>
            <label className="field-label">Email</label>
            <input className="field-input opacity-70" value={info.email} disabled />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="field-label">Prénom</label>
              <input className="field-input" value={info.first_name} onChange={(e) => setInfo({ ...info, first_name: e.target.value })} />
            </div>
            <div>
              <label className="field-label">Nom</label>
              <input className="field-input" value={info.last_name} onChange={(e) => setInfo({ ...info, last_name: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="field-label">Téléphone</label>
            <input className="field-input" value={info.phone} onChange={(e) => setInfo({ ...info, phone: e.target.value })} />
          </div>
          <button onClick={saveInfo} className="btn-primary" data-testid="account-save-info">Enregistrer</button>
        </div>
      )}
    </div>
  );
}
